import { useState, useEffect } from "react";
import { Plus, Trash2, Tags } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCategory, setNewCategory] = useState("");
  const [newTag, setNewTag] = useState("");
  const [deleteItem, setDeleteItem] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [catsRes, tagsRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/tags`),
      ]);
      setCategories(catsRes.data);
      setTags(tagsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;

    try {
      const formData = new FormData();
      formData.append("name", newCategory.trim());
      await axios.post(`${API}/categories`, formData);
      toast.success("Categoría creada");
      setNewCategory("");
      fetchData();
    } catch (error) {
      toast.error("Error al crear categoría");
    }
  };

  const handleAddTag = async (e) => {
    e.preventDefault();
    if (!newTag.trim()) return;

    try {
      const formData = new FormData();
      formData.append("name", newTag.trim());
      await axios.post(`${API}/tags`, formData);
      toast.success("Tag creado");
      setNewTag("");
      fetchData();
    } catch (error) {
      toast.error("Error al crear tag");
    }
  };

  const handleDelete = async () => {
    if (!deleteItem) return;

    try {
      if (deleteItem.type === "category") {
        await axios.delete(`${API}/categories/${deleteItem.id}`, {
        });
        toast.success("Categoría eliminada");
      } else {
        await axios.delete(`${API}/tags/${deleteItem.id}`, {
        });
        toast.success("Tag eliminado");
      }
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
    setDeleteItem(null);
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-categories">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-brand-text">
            Categorías y Tags
          </h1>
          <p className="text-brand-textSecondary mt-1">
            Gestiona las categorías y etiquetas de tu contenido
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Categories */}
          <Card>
            <CardHeader>
              <CardTitle>Categorías</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddCategory} className="flex gap-2 mb-4">
                <Input
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Nueva categoría"
                  data-testid="new-category-input"
                />
                <Button type="submit" data-testid="add-category-btn">
                  <Plus className="h-4 w-4" />
                </Button>
              </form>

              {loading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />
                  ))}
                </div>
              ) : categories.length > 0 ? (
                <div className="space-y-2">
                  {categories.map((cat) => (
                    <div
                      key={cat.category_id}
                      className="flex items-center justify-between p-3 bg-brand-bg rounded-lg"
                      data-testid={`category-${cat.category_id}`}
                    >
                      <div>
                        <p className="font-medium text-brand-text">{cat.name}</p>
                        <p className="text-sm text-brand-textMuted">/{cat.slug}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          setDeleteItem({ type: "category", id: cat.category_id })
                        }
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-8 text-brand-textSecondary">
                  No hay categorías
                </p>
              )}
            </CardContent>
          </Card>

          {/* Tags */}
          <Card>
            <CardHeader>
              <CardTitle>Tags</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddTag} className="flex gap-2 mb-4">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Nuevo tag"
                  data-testid="new-tag-input"
                />
                <Button type="submit" data-testid="add-tag-btn">
                  <Plus className="h-4 w-4" />
                </Button>
              </form>

              {loading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />
                  ))}
                </div>
              ) : tags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <div
                      key={tag.tag_id}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-brand-bg rounded-full"
                      data-testid={`tag-${tag.tag_id}`}
                    >
                      <Tags className="h-3 w-3 text-brand-textMuted" />
                      <span className="text-sm font-medium text-brand-text">
                        {tag.name}
                      </span>
                      <button
                        onClick={() =>
                          setDeleteItem({ type: "tag", id: tag.tag_id })
                        }
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-8 text-brand-textSecondary">
                  No hay tags
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Delete Dialog */}
        <AlertDialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                ¿Eliminar {deleteItem?.type === "category" ? "categoría" : "tag"}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
